import { isTuningExperimentRequest } from '../contracts/experiments';
import type { IExperimentRunner, TuningExperimentRequest, TuningExperimentSnapshot } from '../contracts/experiments';
import type { TuningTelemetryWindow } from '../contracts/measurements';
import type { ResolvedTuningConfiguration, TuningPatch } from '../contracts/settings';
import type { TuningCommand, TuningTrialSnapshot } from '../contracts/identity';
import { TrialOperation } from '../lifecycle/TrialOperation';
import type { ExperimentRunnerOptions } from './OperatorContracts';

type Run = { request: TuningExperimentRequest; state: TuningExperimentSnapshot; controller: AbortController;
  deadlineMs: number; baseline: ResolvedTuningConfiguration; restored: boolean; lastSnapshot?: TuningTrialSnapshot };
type Candidate = { strategyId: string; patch: TuningPatch };

/** Host owns the schedule, absolute deadline and restoration after CLI/UI disconnects. */
export class ExperimentRunner implements IExperimentRunner {
  private readonly runs = new Map<string, Run>();
  private readonly requests = new Map<string, { content: string; id: string }>();
  private readonly active = new Map<string, Run>();
  constructor(private readonly options: ExperimentRunnerOptions) {}

  public async start(request: TuningExperimentRequest): Promise<TuningExperimentSnapshot> {
    if (!isTuningExperimentRequest(request) || !this.options.session.authorize(request)) throw new Error('experiment-unauthorized');
    const planned = request.spec.candidates.length * request.spec.repetitions * (request.spec.kind === 'comparison' ? 3 : 1);
    if (planned + 1 > request.spec.maxFreshAttempts) throw new Error('experiment-restoration-budget');
    const key = `${request.sessionId}:${request.requestId}`; const content = JSON.stringify(request);
    const previous = this.requests.get(key);
    if (previous) {
      if (previous.content !== content) throw new Error('experiment-request-conflict');
      const cached = this.status(previous.id); if (!cached) throw new Error('experiment-replay-expired'); return cached;
    }
    if (this.active.has(request.sessionId)) throw new Error('operation-in-progress');
    const now = this.options.clock.monotonicMs(); const id = this.options.id();
    const deadlineMs = now + request.spec.totalDeadlineMs;
    const baseline = structuredClone(this.options.session.production(request.sessionId));
    if (!this.options.session.acquire(request.sessionId, id, deadlineMs)) throw new Error('operation-in-progress');
    const run: Run = { request: structuredClone(request), baseline, deadlineMs, controller: new AbortController(), restored: false,
      state: { id, sessionId: request.sessionId, requestId: request.requestId, state: 'queued',
        startedAtEpochMs: this.options.clock.wallMs(), deadlineAtEpochMs: this.options.clock.wallMs() + request.spec.totalDeadlineMs, scores: [] } };
    this.active.set(request.sessionId, run); this.runs.set(id, run); this.requests.set(key, { content, id });
    while (this.runs.size > 64) {
      const oldest = [...this.runs].find(([, candidate]) => !this.active.has(candidate.request.sessionId));
      if (!oldest) break; this.runs.delete(oldest[0]);
    }
    while (this.requests.size > 128) this.requests.delete(this.requests.keys().next().value as string);
    if (request.spec.kind === 'qualification') {
      try { this.options.session.renewQualification(request, deadlineMs); }
      catch (error) { this.active.delete(request.sessionId); this.runs.delete(id); this.requests.delete(key);
        this.options.session.release(request.sessionId, id); throw error; }
    }
    this.publish(run); void this.run(run); return this.status(id)!;
  }
  public status(id: string): TuningExperimentSnapshot | undefined { const run = this.runs.get(id); return run && structuredClone(run.state); }
  public async cancel(id: string, reason: string): Promise<TuningExperimentSnapshot> {
    const run = this.runs.get(id); if (!run) throw new Error('unknown-experiment');
    if (this.active.get(run.request.sessionId) !== run) return this.status(id)!;
    run.controller.abort(reason.slice(0, 128)); run.state = { ...run.state, state: 'restoring' }; this.publish(run);
    return this.status(id)!;
  }
  private async run(run: Run): Promise<void> {
    run.state = { ...run.state, state: 'running' }; this.publish(run);
    try {
      const schedule = this.schedule(run);
      for (let index = 0; index < schedule.length; index++) {
        this.assertActive(run);
        const trial = await this.fresh(run, schedule[index], index);
        run.lastSnapshot = trial; run.state = { ...run.state, currentTrialId: trial.target.trialId }; this.publish(run);
        if (trial.phase !== 'settling') {
          if (trial.restoration) run.restored = true;
          throw new Error(trial.error?.code ?? 'replacement-failed');
        }
        await this.wait(run, run.request.spec.settleMs);
        const measurement = await this.measure(run, trial);
        const score = this.options.scorer.score({ snapshot: trial, windows: measurement,
          measurementMs: run.request.spec.measureMs, targets: run.request.spec.targets });
        run.state = { ...run.state, scores: [...run.state.scores, score] }; this.publish(run);
        if (score.result === 'fail' && run.request.spec.restoreOnFailure) throw new Error('measurement-target-breach');
      }
      await this.restore(run);
      run.state = { ...run.state, state: 'completed' }; this.publish(run);
    } catch (error) {
      const cancelled = run.controller.signal.aborted;
      if (run.lastSnapshot?.restoration && run.lastSnapshot.restoration.status !== 'completed') {
        run.state = { ...run.state, state: 'failed', error: { code: 'restoration-failed', message: 'Fresh trial restoration did not complete' } };
        this.publish(run); return;
      }
      try { await this.restore(run); }
      catch (restoreError) { run.state = { ...run.state, state: 'failed', error: {
        code: 'restoration-failed', message: (restoreError as Error).message.slice(0, 2048) } }; this.publish(run); return; }
      run.state = { ...run.state, state: cancelled ? 'cancelled' : 'failed', error: {
        code: cancelled ? 'cancelled' : 'experiment-failed', message: (error as Error).message.slice(0, 2048) } }; this.publish(run);
    } finally { this.active.delete(run.request.sessionId); this.options.session.release(run.request.sessionId, run.state.id); }
  }
  private schedule(run: Run): Candidate[] {
    const baseline: Candidate = { strategyId: 'production', patch: { ...run.baseline.settings } };
    const schedule: Candidate[] = [];
    for (let repetition = 0; repetition < run.request.spec.repetitions; repetition++) {
      for (const candidate of run.request.spec.candidates) {
        if (run.request.spec.kind === 'comparison') schedule.push(baseline);
        schedule.push({ strategyId: candidate.strategyId, patch: { ...run.baseline.settings, ...candidate.patch } });
        if (run.request.spec.kind === 'comparison') schedule.push(baseline);
      }
    }
    if (schedule.length > run.request.spec.maxFreshAttempts) throw new Error('experiment-attempt-budget');
    return schedule;
  }
  private async fresh(run: Run, candidate: Candidate, index: number): Promise<TuningTrialSnapshot> {
    const current = this.options.session.snapshot(run.request.sessionId);
    const command: TuningCommand = { version: 1, commandId: `${run.state.id}-${index}`, target: current.target,
      expectedRevision: current.target.configRevision, leaseId: run.request.leaseId, ttlMs: 10_000,
      action: { kind: 'start-fresh', patch: candidate.patch, strategyId: candidate.strategyId, workloadId: run.request.spec.workloadId } };
    const operation = new TrialOperation(this.options.clock, Math.min(15_000, run.deadlineMs - this.options.clock.monotonicMs()));
    try { return await operation.within(() => this.options.session.execute(command, run.state.id), 'experiment fresh trial'); }
    finally { operation.cancel('completed'); }
  }
  private async measure(run: Run, trial: TuningTrialSnapshot): Promise<readonly TuningTelemetryWindow[]> {
    const windows: TuningTelemetryWindow[] = []; let badSince: number | undefined;
    const detach = this.options.session.subscribe(window => {
      if (window.target.sessionId !== trial.target.sessionId || window.target.trialId !== trial.target.trialId
        || window.target.connectionEpoch !== trial.target.connectionEpoch || window.target.configRevision !== trial.target.configRevision) return;
      if (windows.length < 2_000) windows.push(window);
      if (window.endpoint !== 'guest') return;
      const gap = window.metrics['presentationGap.max']; const silence = window.metrics['sinceLastPresent'];
      const bad = (gap?.availability === 'measured' && (gap.value ?? 0) > run.request.spec.targets.maxGapMs)
        || (silence?.availability === 'measured' && (silence.value ?? 0) > run.request.spec.targets.maxGapMs);
      if (bad) badSince ??= this.options.clock.monotonicMs(); else badSince = undefined;
    });
    try {
      const end = this.options.clock.monotonicMs() + run.request.spec.measureMs;
      while (this.options.clock.monotonicMs() < end) {
        await this.wait(run, Math.min(250, end - this.options.clock.monotonicMs()));
        if (badSince !== undefined && this.options.clock.monotonicMs() - badSince >= 2_000) throw new Error('sustained-presentation-breach');
      }
      return windows;
    } finally { detach(); }
  }
  private async restore(run: Run): Promise<void> {
    if (run.restored) return; run.restored = true;
    run.state = { ...run.state, state: 'restoring' }; this.publish(run);
    const operation = new TrialOperation(this.options.clock, 15_000);
    try { await operation.within(() => this.options.session.restoreAccepted(run.request.sessionId, run.state.id, run.baseline), 'experiment baseline restoration'); }
    finally { operation.cancel('completed'); }
  }
  private async wait(run: Run, delayMs: number): Promise<void> {
    this.assertActive(run);
    await this.options.wait(Math.min(delayMs, run.deadlineMs - this.options.clock.monotonicMs()), run.controller.signal);
    this.assertActive(run);
  }
  private assertActive(run: Run): void {
    if (run.controller.signal.aborted) throw new Error('cancelled');
    if (this.options.clock.monotonicMs() >= run.deadlineMs) throw new Error('experiment-deadline');
  }
  private publish(run: Run): void { this.options.session.publish(structuredClone(run.state)); }
}
